<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { reactive, ref } from 'vue'

import SiteCurtailmentConfigurationDialog from '@/components/eos/auto-curtailment/SiteCurtailmentConfigurationDialog.vue'
import RcSiteSelect, { type ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useForemanApiKeys } from '@/composables/core/foreman-api-keys'
import { useSiteCurtailmentConfigurations } from '@/composables/eos/site-curtailment-configurations'
import { useOIDCStore } from '@/stores/oidc.store'
import { SiteType } from '@/types/core/Site'
import type { ISiteCurtailmentConfigurationFilters } from '@/types/eos/filters'
import type { ISiteCurtailmentConfiguration } from '@/types/eos/SiteCurtailmentConfiguration'
import { useRouter } from 'vue-router'

const router = useRouter()

const { isManagerUser } = useOIDCStore()

const { getSiteCurtailmentConfigurationsQuery, deleteSiteCurtailmentConfigurationMutation } =
  useSiteCurtailmentConfigurations()
const { getApiKeyQuery } = useForemanApiKeys()

const upsertDialogVisible = ref<boolean>(false)
const configurationBeingModified = ref<ISiteCurtailmentConfiguration>()

const filters = reactive<ISiteCurtailmentConfigurationFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  site_id: undefined,
})

const onSiteFilterUpdate = (value?: ISiteIdentifier) => {
  filters.site_id = value?.site_id
}

const handleEdit = (configuration: ISiteCurtailmentConfiguration) => {
  configurationBeingModified.value = configuration
  upsertDialogVisible.value = true
}

const { data: apiKeyData } = getApiKeyQuery()
const { isPending, isError, isRefetching, data, error, refetch } = getSiteCurtailmentConfigurationsQuery(filters)
</script>

<template>
  <ViewLayout>
    <template v-if="apiKeyData" #controls>
      <el-button type="primary" :disabled="isPending" @click="upsertDialogVisible = true">Create</el-button>
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <template v-if="apiKeyData">
        <div class="flex inline-flex gap-4">
          <RcSiteSelect
            placeholder="Site ID or Name"
            :type="SiteType.DataCenter"
            :disabled="isPending"
            style="width: 200px"
            @change="onSiteFilterUpdate"
          />
        </div>
        <el-table v-loading="isPending" :data="data?.data" empty-text="No data" stripe>
          <el-table-column type="expand">
            <template #default="scope">
              <el-table :data="scope.row.client_configurations" empty-text="No data">
                <el-table-column label="Client Configurations" header-align="center">
                  <el-table-column prop="hosting_client.name" label="Hosting Client" />
                  <el-table-column label="Foreman Curtailment Plans">
                    <template #default="scope">
                      <p
                        v-for="curtailmentPlan in scope.row.curtailment_plans"
                        :key="curtailmentPlan.foreman_curtailment_plan_id"
                      >
                        [ID {{ curtailmentPlan.foreman_curtailment_plan_id }}]
                        {{ curtailmentPlan.foreman_curtailment_plan_name }}
                      </p>
                    </template>
                  </el-table-column>
                </el-table-column>
              </el-table>
            </template>
          </el-table-column>
          <el-table-column prop="site.name" label="Site" />
          <el-table-column label="Configured Clients">
            <template #default="scope">
              <div class="flex flex-row gap-2">
                <el-tag v-for="client in scope.row.client_configurations" :key="client.hosting_client.id">
                  {{ client.hosting_client.name }}
                </el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="70" align="right">
            <template #default="scope">
              <el-button link title="Edit" @click="handleEdit(scope.row as ISiteCurtailmentConfiguration)">
                <Icon icon="material-symbols:edit-outline-rounded" inline height="20" />
              </el-button>
              <el-button
                link
                title="Delete"
                @click="
                  DeleteConfirmationModal(
                    'Delete Configuration',
                    'Are you sure you want to delete this curtailment configuration? This action cannot be undone',
                    scope.row.id,
                    deleteSiteCurtailmentConfigurationMutation.mutateAsync
                  )
                "
              >
                <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-if="data && data.total_pages > 0"
          v-model:page-size="filters.per_page"
          v-model:current-page="filters.page"
          class="self-end"
          :page-sizes="DEFAULT_PAGE_SIZES"
          :page-count="data.total_pages"
          :total="data.total_records"
          :disabled="isPending"
          :layout="DEFAULT_PAGINATION_LAYOUT"
          background
        />
      </template>
      <template v-else>
        <RlEmptyDataset
          title="Foreman API key not found"
          subtitle="You need to configure a Foreman API key to start using this feature"
        >
          <el-button
            v-if="isManagerUser"
            type="primary"
            @click="() => router.push({ name: 'core-foreman-configuration' })"
            >Configure</el-button
          >
        </RlEmptyDataset>
      </template>
    </template>
    <SiteCurtailmentConfigurationDialog
      v-model="upsertDialogVisible"
      :site-curtailment-configuration="configurationBeingModified"
      @close="configurationBeingModified = undefined"
    />
  </ViewLayout>
</template>
